
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, FileText, CheckCircle, DollarSign, Download, Clock, Calendar, BarChart2, LineChart, PieChart } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import StatisticsOverviewCards from '@/components/statistics/StatisticsOverviewCards';
import CaseConversionChart from '@/components/statistics/CaseConversionChart';
import IncomeChart from '@/components/statistics/IncomeChart';
import AdPerformanceStats from '@/components/statistics/AdPerformanceStats';
import AdPerformanceInsights from '@/components/statistics/AdPerformanceInsights';
import PatientSourceChart from '@/components/statistics/PatientSourceChart';
import PerformanceSuggestions from '@/components/statistics/PerformanceSuggestions';
import PerformanceScoreCard from '@/components/statistics/PerformanceScoreCard';
import { useStatisticsData } from '@/hooks/useStatisticsData';
import { useDoctorSuggestions } from '@/hooks/useDoctorSuggestions';
import { useSuggestionDismissal } from '@/hooks/useSuggestionDismissal';
import { useDoctorScore } from '@/hooks/useScores';

// New components:
import ServicePerformanceTable from '@/components/statistics/ServicePerformanceTable';
import PatientAnalytics from '@/components/statistics/PatientAnalytics';
import TimeSessionInsights from '@/components/statistics/TimeSessionInsights';

const DoctorStatisticsPage = () => {
  const [dateRange, setDateRange] = useState('30days');
  const { 
    overviewStats, 
    conversionData, 
    incomeData, 
    adPerformance, 
    patientSourceData,
    isAdsActive,
    adInsights
  } = useStatisticsData(dateRange);

  // In a real app, this would use the authenticated doctor's ID
  const { score: doctorScore, isLoading: scoreLoading } = useDoctorScore('doctor-1');

  // Mock content data for suggestions (in a real app, this would come from API)
  const contentData = {
    daysSinceLastStory: 9,
    daysSinceLastBeforeAfter: 15,
    reviewScore: 4.7,
    reviewCount: 8
  };

  // Get performance suggestions
  const suggestions = useDoctorSuggestions(
    { overviewStats, conversionData, incomeData, adPerformance, patientSourceData, isAdsActive, adInsights }, 
    contentData
  );
  
  // Setup dismissal tracking - in a real app, use the actual doctor ID
  const { dismissSuggestion, isSuggestionDismissed } = useSuggestionDismissal('doctor-123');
  
  // Filter out dismissed suggestions
  const activeSuggestions = suggestions.filter(suggestion => !isSuggestionDismissed(suggestion.id));

  const handleExportPdf = () => {
    alert('Generating PDF report... This feature is coming soon!');
    // PDF export logic would be implemented here
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="container mx-auto p-4 pb-24 pt-20">
        <div className="flex flex-wrap items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">My Statistics</h1>
            <p className="text-muted-foreground">Track your performance metrics and patient engagement</p>
          </div>
          
          <div className="flex items-center gap-2 mt-4 sm:mt-0">
            <Select value={dateRange} onValueChange={(value) => setDateRange(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7days">Last 7 days</SelectItem>
                <SelectItem value="30days">Last 30 days</SelectItem>
                <SelectItem value="90days">Last 90 days</SelectItem>
                <SelectItem value="thismonth">This month</SelectItem>
                <SelectItem value="thisyear">This year</SelectItem>
                <SelectItem value="alltime">All time</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={handleExportPdf} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </div>
        
        {/* Overview Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <Users className="h-6 w-6 text-primary mb-2" />
                <div className="text-2xl font-bold">{overviewStats.totalPatients}</div>
                <p className="text-xs text-muted-foreground">
                  Total Patients
                </p>
                <p className="text-xs text-green-600">
                  +{overviewStats.patientsChange}%
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <Calendar className="h-6 w-6 text-indigo-500 mb-2" />
                <div className="text-2xl font-bold">54</div>
                <p className="text-xs text-muted-foreground">
                  Appointments
                </p>
                <p className="text-xs text-green-600">
                  +12%
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <CheckCircle className="h-6 w-6 text-green-500 mb-2" />
                <div className="text-2xl font-bold">83%</div>
                <p className="text-xs text-muted-foreground">
                  Follow-up Rate
                </p>
                <p className="text-xs text-green-600">
                  +5%
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <Clock className="h-6 w-6 text-amber-500 mb-2" />
                <div className="text-2xl font-bold">4.2</div>
                <p className="text-xs text-muted-foreground">
                  Avg. Sessions
                </p>
                <p className="text-xs text-muted-foreground">
                  Per Patient
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <FileText className="h-6 w-6 text-blue-500 mb-2" />
                <div className="text-2xl font-bold">{Math.round(conversionData.plansAcceptedPercent)}%</div>
                <p className="text-xs text-muted-foreground">
                  Acceptance Rate
                </p>
                <p className="text-xs text-green-600">
                  +{overviewStats.plansAcceptedChange}%
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Performance Score Card */}
        {doctorScore && !scoreLoading && (
          <div className="mb-6">
            <PerformanceScoreCard score={doctorScore} isDoctor={true} />
          </div>
        )}
        
        {/* Performance Suggestions Section */}
        {activeSuggestions.length > 0 && (
          <div className="mb-6">
            <PerformanceSuggestions 
              suggestions={activeSuggestions} 
              onDismiss={dismissSuggestion}
            />
          </div>
        )}
        
        {/* Patient Analytics Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <LineChart className="h-5 w-5 mr-2 text-primary" />
              Patient Analytics
            </CardTitle>
            <CardDescription>Monthly growth and geographical distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <PatientAnalytics />
          </CardContent>
        </Card>
        
        {/* Service Performance Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart2 className="h-5 w-5 mr-2 text-primary" />
              Service Performance
            </CardTitle>
            <CardDescription>Profitability and popularity of your services</CardDescription>
          </CardHeader>
          <CardContent>
            <ServicePerformanceTable />
          </CardContent>
        </Card>
        
        {/* Time & Session Insights */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <PieChart className="h-5 w-5 mr-2 text-primary" />
              Time & Session Insights
            </CardTitle>
            <CardDescription>Booking patterns and session statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <TimeSessionInsights />
          </CardContent>
        </Card>
        
        {/* Conversion Funnel */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <LineChart className="h-5 w-5 mr-2 text-primary" />
              Case Conversion Funnel
            </CardTitle>
            <CardDescription>Patient journey from first case to completion</CardDescription>
          </CardHeader>
          <CardContent>
            <CaseConversionChart data={conversionData} />
          </CardContent>
        </Card>
        
        {/* Income Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-primary" />
              Income Summary
            </CardTitle>
            <CardDescription>Monthly income breakdown and trends</CardDescription>
          </CardHeader>
          <CardContent>
            <IncomeChart data={incomeData} />
            <div className="mt-4 text-right">
              <p className="text-sm text-muted-foreground">Year-to-date income</p>
              <p className="text-xl font-bold">${incomeData.ytdTotal.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        
        {/* Detailed Ad Performance - Only shown if doctor has ads */}
        {isAdsActive && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-primary" />
                Ads Performance Details
              </CardTitle>
              <CardDescription>Impact of your promotional activities</CardDescription>
            </CardHeader>
            <CardContent>
              <AdPerformanceStats data={adPerformance} />
            </CardContent>
          </Card>
        )}
        
        {/* Ad Performance Insights */}
        {isAdsActive && adInsights && (
          <AdPerformanceInsights stats={adInsights} />
        )}
        
        {/* Patient Source Insights */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2 text-primary" />
              Patient Source Insights
            </CardTitle>
            <CardDescription>Where your patients come from</CardDescription>
          </CardHeader>
          <CardContent>
            <PatientSourceChart data={patientSourceData} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorStatisticsPage;
